import { useRef, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useScroll } from "@react-three/drei";
import * as THREE from "three";
import { TILE_POSITIONS } from "../../constants/projectsConstants";
import { useScrollContext } from "../../contexts/ScrollContext";

const UnifiedScrollHandler = ({ particleTextRef, onSceneChange }) => {
  const data = useScroll();
  const { camera } = useThree();
  const prevScrollRef = useRef(0);
  const currentSceneRef = useRef("home");
  const isJumpingRef = useRef(false); // Track if we're in a jump transition
  const jumpTargetRef = useRef(null); // Store the target scroll position during jump
  const homeCameraProgressRef = useRef(0); // Track smooth camera progress in home zone
  const transitionFrameCountRef = useRef(0); // Count frames during transition
  const scrollDirectionRef = useRef("down"); // Track scroll direction: 'up' or 'down'
  const prevDOMScrollRef = useRef(0); // Track actual DOM scroll position for accurate direction
  const { setScrollProgress, setCurrentSection, currentSection } =
    useScrollContext();

  // Helper function for tile camera positioning
  const getCameraTransformForTile = (tilePosition, tileIndex) => {
    const tile = TILE_POSITIONS[tileIndex];
    const tileRotation = tile.rotation;

    const tileMatrix = new THREE.Matrix4();
    const tileEuler = new THREE.Euler(
      tileRotation[0],
      tileRotation[1],
      tileRotation[2],
      "XYZ",
    );
    tileMatrix.makeRotationFromEuler(tileEuler);

    const frontNormal = new THREE.Vector3(0, 0, -1);
    frontNormal.applyMatrix4(tileMatrix);
    frontNormal.normalize();

    const upDirection = new THREE.Vector3(0, 1, 0);
    upDirection.applyMatrix4(tileMatrix);
    upDirection.normalize();

    const viewingDistance = 15;
    const cameraPosition = new THREE.Vector3(...tilePosition).add(
      frontNormal.clone().multiplyScalar(viewingDistance),
    );

    const cameraMatrix = new THREE.Matrix4();
    cameraMatrix.lookAt(
      cameraPosition,
      new THREE.Vector3(...tilePosition),
      upDirection,
    );

    const cameraRotation = new THREE.Euler();
    cameraRotation.setFromRotationMatrix(cameraMatrix);

    return {
      position: cameraPosition,
      rotation: cameraRotation,
    };
  };

  // Listen for scroll-to-section events from navbar
  useEffect(() => {
    const handleScrollToSection = (event) => {
      const { section } = event.detail;

      // Safety check: ensure scroll element is ready
      if (!data || !data.el) {
        console.warn("Scroll element not ready yet, retrying in 100ms...");
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("scrollToSection", { detail: event.detail }),
          );
        }, 100);
        return;
      }

      if (data && data.el) {
        let targetScroll = 0;

        const homeZoneEnd = 2 / 8; // 0.25 - doubled the home space for more scroll room
        const tilesZoneEnd = 5 / 8; // 0.625
        const aboutZoneEnd = 7.5 / 8; // 0.875 - 2 pages for about section
        const transitionBuffer = 0.05;

        switch (section) {
          case "home":
            targetScroll = 0;
            currentSceneRef.current = "home";
            setCurrentSection("home");
            // Reset camera progress to zoomed out position
            homeCameraProgressRef.current = 0;
            transitionFrameCountRef.current = 0;
            if (particleTextRef?.current?.setVisible) {
              particleTextRef.current.setVisible(true);
            }
            camera.position.set(0, 0, 100);
            camera.lookAt(0, 0, 0);
            camera.rotation.set(0, 0, 0);
            if (onSceneChange) onSceneChange("home");
            break;
          case "projects":
            targetScroll = homeZoneEnd + transitionBuffer;
            currentSceneRef.current = "projects";
            setCurrentSection("projects");
            if (particleTextRef?.current?.setVisible) {
              particleTextRef.current.setVisible(false);
            }
            if (TILE_POSITIONS.length > 0) {
              const firstTileTransform = getCameraTransformForTile(
                TILE_POSITIONS[0].position,
                0,
              );
              camera.position.copy(firstTileTransform.position);
              camera.quaternion.setFromEuler(firstTileTransform.rotation);
            }
            if (onSceneChange) onSceneChange("projects");
            break;
          case "about":
            targetScroll = tilesZoneEnd + transitionBuffer;
            console.log(
              "🔵 About navigation - targetScroll:",
              targetScroll,
              "in vh:",
              targetScroll * 800,
            );
            currentSceneRef.current = "about";
            setCurrentSection("about");
            if (particleTextRef?.current?.setVisible) {
              particleTextRef.current.setVisible(false);
            }
            // Set camera to neutral about position
            camera.position.set(0, 10, 30);
            camera.lookAt(0, 0, 0);
            if (onSceneChange) onSceneChange("about");
            break;
          case "contact":
            targetScroll = aboutZoneEnd + transitionBuffer;
            console.log(
              "🟢 Contact navigation - targetScroll:",
              targetScroll,
              "in vh:",
              targetScroll * 800,
            );
            currentSceneRef.current = "contact";
            setCurrentSection("contact");
            if (particleTextRef?.current?.setVisible) {
              particleTextRef.current.setVisible(false);
            }
            if (onSceneChange) onSceneChange("contact");
            break;
        }

        // Mark that we're jumping and store target
        isJumpingRef.current = true;
        jumpTargetRef.current = targetScroll;

        // Don't update prevScrollRef immediately since we're animating
        setScrollProgress(targetScroll);

        // Set scroll position with FAST smooth animation for Scroll html to update transforms
        const scrollElement = data.el;
        const targetScrollTop =
          targetScroll *
          (scrollElement.scrollHeight - scrollElement.clientHeight);
        const currentScrollTop = scrollElement.scrollTop;

        console.log(
          "✅ Starting scroll animation - from:",
          currentScrollTop,
          "to:",
          targetScrollTop,
        );

        // Animate scroll over a short duration using requestAnimationFrame
        const duration = 150; // 150ms - fast but enough for Scroll html to update
        const startTime = performance.now();
        const scrollDiff = targetScrollTop - currentScrollTop;

        const animateScroll = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Ease out cubic for smooth deceleration
          const easeOutCubic = 1 - Math.pow(1 - progress, 3);

          const newScrollTop = currentScrollTop + scrollDiff * easeOutCubic;
          scrollElement.scrollTop = newScrollTop;

          if (progress < 1) {
            requestAnimationFrame(animateScroll);
          } else {
            // Update data.offset after animation completes
            if (data.offset !== undefined) {
              data.offset = targetScroll;
            }
            console.log(
              "✅ Scroll animation complete - final position:",
              scrollElement.scrollTop,
            );
          }
        };

        requestAnimationFrame(animateScroll);

        // Keep jumping flag active during the scroll animation
        setTimeout(() => {
          isJumpingRef.current = false;
          jumpTargetRef.current = null;
          console.log(
            "📍 After jump - scroll position:",
            scrollElement.scrollTop,
            "data.offset:",
            data.offset,
          );
        }, 200); // Wait for animation to complete (150ms) + buffer
      }
    };

    window.addEventListener("scrollToSection", handleScrollToSection);
    return () =>
      window.removeEventListener("scrollToSection", handleScrollToSection);
  }, [
    data,
    camera,
    particleTextRef,
    onSceneChange,
    setCurrentSection,
    setScrollProgress,
  ]);

  useFrame(() => {
    if (!data) return;

    // Skip frame updates ONLY during jump transitions
    if (isJumpingRef.current) {
      // But still update prevScrollRef to track position
      prevScrollRef.current = data.offset;
      return;
    }

    const scroll = data.offset;

    // Track scroll direction using ACTUAL DOM scroll position (not damped offset)
    // This is more accurate because the damped offset can overshoot/oscillate
    const scrollElement = data?.el;
    const currentDOMScroll = scrollElement?.scrollTop || 0;

    if (currentDOMScroll < prevDOMScrollRef.current - 1) {
      // -1 for tolerance
      scrollDirectionRef.current = "up";
    } else if (currentDOMScroll > prevDOMScrollRef.current + 1) {
      // +1 for tolerance
      scrollDirectionRef.current = "down";
    }
    prevDOMScrollRef.current = currentDOMScroll;

    const homeZoneEnd = 2 / 8; // 0.25 - doubled the home space for more scroll room
    const tilesZoneEnd = 5 / 8; // 0.625
    const aboutZoneEnd = 7.5 / 8; // 0.875 - 2 pages for about section
    const transitionBuffer = 0.01;

    if (scroll <= homeZoneEnd + transitionBuffer) {
      if (currentSceneRef.current !== "home") {
        const previousScene = currentSceneRef.current;
        currentSceneRef.current = "home";
        setCurrentSection("home");
        setScrollProgress(scroll);

        if (particleTextRef?.current?.setVisible) {
          particleTextRef.current.setVisible(true);
        }

        if (onSceneChange) onSceneChange("home");

        // Check actual scroll position
        const scrollElement = data?.el;
        const actualScrollTop = scrollElement?.scrollTop || 0;

        // Check if scroll position changes in next frame
        setTimeout(() => {
          const newScrollTop = scrollElement?.scrollTop || 0;
          if (Math.abs(newScrollTop - actualScrollTop) > 10) {
            console.warn(
              `⚠️ SCROLL JUMPED! Was ${actualScrollTop.toFixed(0)}, now ${newScrollTop.toFixed(0)}`,
            );
          }
        }, 100);
      }

      // Handle home scene camera control - camera should smoothly follow scroll
      handleHomeCamera(
        scroll,
        homeZoneEnd,
        transitionBuffer,
        scrollDirectionRef.current,
      );
    } else if (scroll <= tilesZoneEnd + transitionBuffer) {
      if (currentSceneRef.current !== "projects") {
        const previousScene = currentSceneRef.current;
        currentSceneRef.current = "projects";
        setCurrentSection("projects");
        setScrollProgress(scroll);

        if (particleTextRef?.current?.setVisible) {
          particleTextRef.current.setVisible(false);
        }

        if (onSceneChange) onSceneChange("projects");
      }

      handleProjectsCamera(scroll, homeZoneEnd, transitionBuffer, tilesZoneEnd);
    } else if (scroll <= aboutZoneEnd + transitionBuffer) {
      if (currentSceneRef.current !== "about") {
        currentSceneRef.current = "about";
        setCurrentSection("about");
        setScrollProgress(scroll);
        if (onSceneChange) onSceneChange("about");
      }

      // Keep camera at last tile position or transition to a neutral view
      handleAboutCamera(scroll, tilesZoneEnd, aboutZoneEnd);
    } else {
      if (currentSceneRef.current !== "contact") {
        currentSceneRef.current = "contact";
        setCurrentSection("contact");
        setScrollProgress(scroll);
        if (onSceneChange) onSceneChange("contact");
      }
    }

    prevScrollRef.current = scroll;
  });

  const handleHomeCamera = (
    scroll,
    homeZoneEnd,
    transitionBuffer = 0.01,
    scrollDirection = "down",
  ) => {
    // Normalize scroll value within the "home" zone
    const effectiveHomeEnd = homeZoneEnd + transitionBuffer;

    // Calculate the raw progress through the home zone
    // ALWAYS: scroll 0 = zoomed out, scroll at homeZoneEnd = zoomed in
    let progress = THREE.MathUtils.clamp(scroll / effectiveHomeEnd, 0, 1);

    // Easing for smoother motion
    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const eased = easeInOutCubic(progress);

    // Define zoom range - responsive based on screen size
    const zoomOutPos = new THREE.Vector3(0, 0, 100); // top of page (zoomed out)
    const isMobile = window.innerWidth < 768;
    const zoomInPos = isMobile
      ? new THREE.Vector3(3, 25, 10) // Mobile: lower Y position
      : new THREE.Vector3(3, 35, 10); // Desktop: higher Y position

    // Directly compute camera position based on scroll
    const newPos = new THREE.Vector3().lerpVectors(
      zoomOutPos,
      zoomInPos,
      eased,
    );
    camera.position.copy(newPos);

    // Always look at the scene center
    camera.lookAt(0, 0, 0);
    camera.rotation.set(0, 0, 0);

    // Optional: adjust FOV directly based on scroll
    const startFOV = 75; // top
    const endFOV = 60; // bottom
    camera.fov = THREE.MathUtils.lerp(startFOV, endFOV, eased);
    camera.updateProjectionMatrix();

    // Update progress ref (optional, for other logic)
    homeCameraProgressRef.current = progress;
  };

  // Projects camera control function
  const handleProjectsCamera = (
    scroll,
    homeZoneEnd,
    transitionBuffer,
    tilesZoneEnd,
  ) => {
    const effectiveTilesStart = homeZoneEnd + transitionBuffer;
    const tilesRange = tilesZoneEnd - effectiveTilesStart;
    const tilesProgress = THREE.MathUtils.clamp(
      (scroll - effectiveTilesStart) / tilesRange,
      0,
      1,
    );

    const exactTileIndex = tilesProgress * (TILE_POSITIONS.length - 1);
    const lowerIndex = Math.floor(exactTileIndex);
    const upperIndex = Math.min(lowerIndex + 1, TILE_POSITIONS.length - 1);
    const t = THREE.MathUtils.smoothstep(exactTileIndex - lowerIndex, 0, 1);

    const lowerTransform = getCameraTransformForTile(
      TILE_POSITIONS[lowerIndex].position,
      lowerIndex,
    );
    const upperTransform = getCameraTransformForTile(
      TILE_POSITIONS[upperIndex].position,
      upperIndex,
    );

    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const smoothedFactor = easeInOutCubic(t);

    const currentCameraPos = new THREE.Vector3().lerpVectors(
      lowerTransform.position,
      upperTransform.position,
      smoothedFactor,
    );

    const lowerQuaternion = new THREE.Quaternion().setFromEuler(
      lowerTransform.rotation,
    );
    const upperQuaternion = new THREE.Quaternion().setFromEuler(
      upperTransform.rotation,
    );
    const currentQuaternion = new THREE.Quaternion().slerpQuaternions(
      lowerQuaternion,
      upperQuaternion,
      smoothedFactor,
    );

    camera.position.copy(currentCameraPos);
    camera.quaternion.copy(currentQuaternion);
  };

  // About camera control function - smoothly transition from last tile to a neutral view
  const handleAboutCamera = (scroll, tilesZoneEnd, aboutZoneEnd) => {
    const aboutZoneRange = aboutZoneEnd - tilesZoneEnd;
    const aboutProgress = THREE.MathUtils.clamp(
      (scroll - tilesZoneEnd) / aboutZoneRange,
      0,
      1,
    );

    // Get the last tile's camera transform
    const lastTileIndex = TILE_POSITIONS.length - 1;
    const lastTileTransform = getCameraTransformForTile(
      TILE_POSITIONS[lastTileIndex].position,
      lastTileIndex,
    );

    // Define a neutral camera position for the about section
    const neutralPos = new THREE.Vector3(0, 10, 30);
    const neutralRotation = new THREE.Euler(0, 0, 0);

    // Smooth easing
    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const smoothedProgress = easeInOutCubic(aboutProgress);

    // Interpolate position
    const currentPos = new THREE.Vector3().lerpVectors(
      lastTileTransform.position,
      neutralPos,
      smoothedProgress,
    );

    // Interpolate rotation
    const lastTileQuaternion = new THREE.Quaternion().setFromEuler(
      lastTileTransform.rotation,
    );
    const neutralQuaternion = new THREE.Quaternion().setFromEuler(
      neutralRotation,
    );
    const currentQuaternion = new THREE.Quaternion().slerpQuaternions(
      lastTileQuaternion,
      neutralQuaternion,
      smoothedProgress,
    );

    camera.position.copy(currentPos);
    camera.quaternion.copy(currentQuaternion);
  };

  return null;
};

export default UnifiedScrollHandler;
